import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMarketDataHistory,
  getMarketDataStatus,
  type MarketDataCommoditySymbol,
  type MarketDataFxPair,
  type MarketDataHistoryDay,
  type MarketDataStatus,
} from '../../api/dataStatusApi';
import { useMarketDataEvents } from '../../hooks/useMarketDataEvents';
import { ApiError } from '../../api/client';
import '../GovernanceLive/governanceLive.css';

// 권위: ARCH_DASHBOARD.md v1.8 §4.15.
// - FX 4종 (USDKRW/JPYKRW/EURKRW/CNYKRW) + Commodity 4종 (WTI/BRENT/GOLD/SILVER).
// - STOMP /topic/system-events DATA.FX_RATES.* / DATA.COMMODITY_PRICES.* 필터.
// - 폴링 fallback: 60초. 장 전 07시 전후엔 10초 (동적 조정은 후속 폴리싱).

const EXPECTED_FX = ['USDKRW', 'JPYKRW', 'EURKRW', 'CNYKRW'];
const EXPECTED_COMMODITY = ['WTI', 'BRENT', 'GOLD_SPOT', 'SILVER_SPOT'];

function formatKst(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso ?? '—';
  return d.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
}

function changeClass(pct: number | null | undefined): string {
  if (pct === null || pct === undefined || pct === 0) return 'mkt-card__change--flat';
  if (pct > 0) return 'mkt-card__change--up';
  return 'mkt-card__change--down';
}

function bannerTone(
  data: MarketDataStatus | undefined,
): 'gov-banner--ok' | 'gov-banner--warn' | 'gov-banner--danger' | '' {
  if (!data) return '';
  const fxFailed = data.fx.failed_count > 0;
  const cmFailed = data.commodity.failed_count > 0;
  if (fxFailed || cmFailed) return 'gov-banner--danger';
  const fxStale = data.fx.stale_count > 0;
  const cmStale = data.commodity.stale_count > 0;
  if (fxStale || cmStale) return 'gov-banner--warn';
  return 'gov-banner--ok';
}

export default function MarketDataStatusPage(): JSX.Element {
  const qc = useQueryClient();

  const statusQuery = useQuery<MarketDataStatus, ApiError>({
    queryKey: ['data', 'market-status'],
    queryFn: () => getMarketDataStatus(),
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
  });

  const historyQuery = useQuery<MarketDataHistoryDay[], ApiError>({
    queryKey: ['data', 'market-history', 7],
    queryFn: () => getMarketDataHistory(7),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const onEvent = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['data', 'market-status'] });
    qc.invalidateQueries({ queryKey: ['data', 'market-history', 7] });
  }, [qc]);
  useMarketDataEvents(onEvent);

  const data = statusQuery.data;
  const history = historyQuery.data ?? [];

  return (
    <div className="gov-page">
      <header className="gov-page__header">
        <h1 className="gov-page__title">Morning Market Data 파이프라인</h1>
        <div className="gov-page__actions">
          <Link to="/logs?eventCode=DATA.FX_RATES">FX 로그</Link>
          <Link to="/logs?eventCode=DATA.COMMODITY_PRICES">Commodity 로그</Link>
        </div>
      </header>

      {statusQuery.isLoading && (
        <div className="gov-muted" style={{ padding: 24 }}>로딩 중…</div>
      )}

      {statusQuery.isError && (
        <div className="gov-error" role="alert">
          시장 데이터 상태 조회 실패: {statusQuery.error?.message ?? 'unknown'}
        </div>
      )}

      {data && (
        <>
          {/* 상단 배너 */}
          <section className={`gov-banner ${bannerTone(data)}`}>
            <div className="gov-banner__left">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div className="gov-banner__sub">as_of {data.as_of_date}</div>
                {data.requested_date && data.effective_date &&
                  data.requested_date !== data.effective_date && (
                  <div className="gov-banner__sub">
                    휴장일 (asked={data.requested_date}, showing={data.effective_date})
                  </div>
                )}
                <h2 className="gov-banner__title">Morning Market Data</h2>
                <div className="gov-banner__sub">
                  FX {data.fx.complete_count}/4 · Commodity{' '}
                  {data.commodity.complete_count}/4
                </div>
              </div>
            </div>
            <div className="gov-banner__right">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <span className="gov-banner__sub">
                  FX last update {formatKst(data.last_batch_event.fx_updated_at)}
                </span>
                <span className="gov-banner__sub">
                  Commodity last update {formatKst(data.last_batch_event.commodity_updated_at)}
                </span>
              </div>
            </div>
          </section>

          {/* 섹션 A — FX 4종 카드 */}
          <section className="gov-card">
            <h2 className="gov-card__title">섹션 A · FX 4종</h2>
            <div className="gov-grid-4">
              {EXPECTED_FX.map((pair) => {
                const row =
                  data.fx.pairs.find((p) => p.currency_pair === pair) ?? null;
                return <FxCard key={pair} pair={pair} row={row} />;
              })}
            </div>
          </section>

          {/* 섹션 B — Commodity 4종 카드 */}
          <section className="gov-card">
            <h2 className="gov-card__title">섹션 B · Commodity 4종</h2>
            <div className="gov-grid-4">
              {EXPECTED_COMMODITY.map((sym) => {
                const row =
                  data.commodity.symbols.find((s) => s.symbol === sym) ?? null;
                return <CommodityCard key={sym} symbol={sym} row={row} />;
              })}
            </div>
          </section>
        </>
      )}

      {/* 섹션 C — 소스 분포 (최근 7일, 간이 테이블) */}
      <section className="gov-card">
        <h2 className="gov-card__title">섹션 C · 소스 분포 (최근 7일)</h2>
        {historyQuery.isLoading && <div className="gov-muted">로딩 중…</div>}
        {historyQuery.isError && (
          <div className="gov-error">
            이력 조회 실패: {historyQuery.error?.message ?? 'unknown'}
          </div>
        )}
        {historyQuery.isSuccess && history.length === 0 && (
          <div className="gov-muted">최근 7일 이력이 없습니다.</div>
        )}
        {history.length > 0 && (
          <table className="gov-table">
            <thead>
              <tr>
                <th>as_of_date</th>
                <th>FX 소스</th>
                <th>Commodity 소스</th>
                <th>이벤트</th>
              </tr>
            </thead>
            <tbody>
              {history.map((d) => (
                <tr key={d.as_of_date}>
                  <td>{d.as_of_date}</td>
                  <td>{formatSourceDist(d.fx_source_dist)}</td>
                  <td>{formatSourceDist(d.commodity_source_dist)}</td>
                  <td>
                    <span style={{ display: 'inline-flex', gap: 4 }}>
                      {d.events.slice(0, 6).map((ev) => (
                        <Link
                          key={ev.event_id}
                          to={`/logs/${ev.event_id}`}
                          title={`${ev.event_code} · ${formatKst(ev.occurred_at)}`}
                        >
                          <span
                            className={`mkt-dot ${
                              ev.severity === 'ERROR'
                                ? 'mkt-dot--fatal'
                                : ev.severity === 'WARN'
                                  ? 'mkt-dot--warn'
                                  : 'mkt-dot--ok'
                            }`}
                          />
                        </Link>
                      ))}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* 섹션 E — 메타 */}
      <section className="gov-card">
        <h2 className="gov-card__title">섹션 E · 파이프라인 메타</h2>
        <table className="gov-table">
          <tbody>
            <tr>
              <th style={{ width: '30%' }}>배치 스케줄</th>
              <td>매일 07:00 KST (장 전)</td>
            </tr>
            <tr>
              <th>실행 주체</th>
              <td>
                <code>src/data/morning_market_data_batch.py</code>
              </td>
            </tr>
            <tr>
              <th>MorningContext 공급</th>
              <td>B 블록 (ARCH_AGENT_GOVERNANCE)</td>
            </tr>
            <tr>
              <th>소스 문서</th>
              <td>ARCH_DATA v1.8 §5.13.3</td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  );
}

function formatSourceDist(dist: Record<string, number>): string {
  const entries = Object.entries(dist);
  if (entries.length === 0) return '—';
  return entries.map(([k, v]) => `${k}:${v}`).join(' · ');
}

function FxCard({
  pair,
  row,
}: {
  pair: string;
  row: MarketDataFxPair | null;
}): JSX.Element {
  if (!row) {
    return (
      <div className="mkt-card mkt-card--failed">
        <div className="mkt-card__head">
          <span className="mkt-card__name">{pair}</span>
          <span className="mkt-dot mkt-dot--fatal" />
        </div>
        <span className="mkt-card__value">—</span>
        <div className="mkt-card__meta">수집 실패 / 미갱신</div>
      </div>
    );
  }
  const cls = row.is_stale ? 'mkt-card--stale' : '';
  return (
    <div className={`mkt-card ${cls}`}>
      <div className="mkt-card__head">
        <span className="mkt-card__name">{pair}</span>
        <span className={`mkt-dot ${row.is_stale ? 'mkt-dot--warn' : 'mkt-dot--ok'}`} />
      </div>
      <span className="mkt-card__value">
        {row.rate !== null ? row.rate.toFixed(2) : '—'}
      </span>
      <div className={`mkt-card__meta ${changeClass(row.change_pct)}`}>
        {row.change_pct !== null
          ? `${row.change_pct > 0 ? '+' : ''}${row.change_pct.toFixed(2)}%`
          : '—'}
      </div>
      <div className="mkt-card__meta">
        <span>source {row.source}</span>
        <span>prev {row.prev_rate !== null ? row.prev_rate.toFixed(2) : '—'}</span>
      </div>
      <div className="mkt-card__meta">fetched {formatKst(row.fetched_at)}</div>
    </div>
  );
}

function CommodityCard({
  symbol,
  row,
}: {
  symbol: string;
  row: MarketDataCommoditySymbol | null;
}): JSX.Element {
  if (!row) {
    return (
      <div className="mkt-card mkt-card--failed">
        <div className="mkt-card__head">
          <span className="mkt-card__name">{symbol}</span>
          <span className="mkt-dot mkt-dot--fatal" />
        </div>
        <span className="mkt-card__value">—</span>
        <div className="mkt-card__meta">수집 실패 / 미갱신</div>
      </div>
    );
  }
  const cls = row.is_stale ? 'mkt-card--stale' : '';
  return (
    <div className={`mkt-card ${cls}`}>
      <div className="mkt-card__head">
        <span className="mkt-card__name">{symbol}</span>
        <span className={`mkt-dot ${row.is_stale ? 'mkt-dot--warn' : 'mkt-dot--ok'}`} />
      </div>
      <span className="mkt-card__value">
        {row.price !== null ? row.price.toFixed(2) : '—'}
      </span>
      <div className={`mkt-card__meta ${changeClass(row.change_pct)}`}>
        {row.change_pct !== null
          ? `${row.change_pct > 0 ? '+' : ''}${row.change_pct.toFixed(2)}%`
          : '—'}
      </div>
      <div className="mkt-card__meta">
        <span>source {row.source}</span>
        <span>unit {row.unit}</span>
      </div>
      <div className="mkt-card__meta">fetched {formatKst(row.fetched_at)}</div>
    </div>
  );
}
